import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClassroomDto, LinkDto } from '../api/types.js';
import { useApi } from '../api/ApiProvider.js';
import { ResetSettingsButton } from './ResetSettingsButton.js';
import formStyles from './settingsForm.module.css';
import styles from './LinksPage.module.css';

export function LinksPageSettings({ classroom }: { classroom: ClassroomDto }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const classroomId = classroom.id;
  const [url, setUrl] = useState('');

  const linksQuery = useQuery({
    queryKey: ['links', classroomId],
    queryFn: () => api.links.listLinks(classroomId),
  });
  const links = linksQuery.data ?? [];

  const setLinks = (next: LinkDto[]) => queryClient.setQueryData(['links', classroomId], next);

  const add = useMutation({
    mutationFn: (body: { url: string }) => api.links.addLink(classroomId, body),
    onSuccess: () => {
      setUrl('');
      void queryClient.invalidateQueries({ queryKey: ['links', classroomId] });
    },
  });

  const remove = useMutation({
    mutationFn: (linkId: string) => api.links.removeLink(classroomId, linkId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links', classroomId] }),
  });

  const reorder = useMutation({
    mutationFn: (ids: string[]) => api.links.reorderLinks(classroomId, { ids }),
    onSuccess: setLinks,
  });

  const resetAll = useMutation({
    mutationFn: async () => {
      const current = await api.links.listLinks(classroomId);
      await Promise.all(current.map((l) => api.links.removeLink(classroomId, l.id)));
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['links', classroomId] }),
  });

  const busy = add.isPending || remove.isPending || reorder.isPending || resetAll.isPending;

  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) return;
    add.mutate({ url: trimmed });
  }

  function move(link: LinkDto, direction: -1 | 1) {
    const index = links.findIndex((l) => l.id === link.id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= links.length) return;
    const next = [...links];
    [next[index], next[target]] = [next[target]!, next[index]!];
    setLinks(next);
    reorder.mutate(next.map((l) => l.id));
  }

  return (
    <div className={`glass glass--solid ${formStyles.panel}`}>
      <header className={formStyles.header}>
        <div className={formStyles.headerText}>
          <h2 className={formStyles.heading}>Links</h2>
          <p className={formStyles.description}>
            Collect web links before class. In the live Links page you open them one at a time;
            opened links move aside until you reset.
          </p>
        </div>
        <ResetSettingsButton
          pageLabel="Links"
          onReset={() => resetAll.mutate()}
          disabled={resetAll.isPending || linksQuery.isLoading}
        />
      </header>

      <form className={formStyles.form} onSubmit={submit}>
        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="links-url">
            Paste a link
          </label>
          <div className={styles.inlineForm}>
            <input
              id="links-url"
              className={formStyles.input}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/article"
              disabled={add.isPending}
              data-testid="links-url-input"
            />
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={add.isPending || !url.trim()}
              data-testid="links-add"
            >
              Add
            </button>
          </div>
          {add.isError ? (
            <p className={styles.error}>That doesn&rsquo;t look like a valid link.</p>
          ) : null}
        </div>
      </form>

      <h3 className={formStyles.sectionTitle}>Saved links</h3>
      {linksQuery.isLoading ? <p className={formStyles.hint}>Loading links…</p> : null}
      {!linksQuery.isLoading && links.length === 0 ? (
        <p className={styles.empty}>No links yet. Paste one above.</p>
      ) : null}
      <div className={styles.linkList}>
        {links.map((link, index) => (
          <article key={link.id} className={styles.linkRow} data-testid="links-row">
            {link.faviconUrl ? (
              <img className={styles.favicon} src={link.faviconUrl} alt="" loading="lazy" />
            ) : (
              <span className={styles.favicon} aria-hidden="true">
                🔗
              </span>
            )}
            <div className={styles.linkText}>
              <strong>{link.title}</strong>
              <span>{link.url}</span>
            </div>
            <div className={styles.rowActions}>
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => move(link, -1)}
                disabled={busy || index === 0}
                aria-label={`Move ${link.title} up`}
              >
                ↑
              </button>
              <button
                type="button"
                className={styles.iconButton}
                onClick={() => move(link, 1)}
                disabled={busy || index === links.length - 1}
                aria-label={`Move ${link.title} down`}
              >
                ↓
              </button>
              <button
                type="button"
                className={styles.removeButton}
                onClick={() => remove.mutate(link.id)}
                disabled={busy}
              >
                Remove
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
